import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import * as api from '../api/client'
import { useSessionStore } from '../store/session'
import { useAuthStore } from '../store/auth'
import { GoogleIcon, MicrosoftIcon } from '../components/ProviderIcons'
import type {
  AIConfigResponse, AITeamConfig, PersonaInfo, OwnedSessionInfo,
} from '../types'

export default function Lobby() {
  const navigate = useNavigate()
  const setTeam = useSessionStore((s) => s.setTeam)
  const setFacilitator = useSessionStore((s) => s.setFacilitator)
  const { sessionId: joinId } = useParams<{ sessionId?: string }>()

  const [mode, setMode] = useState<'home' | 'create' | 'join'>(
    joinId ? 'join' : 'home',
  )

  return (
    <div className="max-w-4xl mx-auto p-8">
      <header className="mb-10">
        <h1 className="text-3xl font-bold text-brand-700">
          AI Transformation Flight Simulator
        </h1>
        <p className="text-slate-600 mt-1">
          A System-Dynamics Strategy Game for Leadership in the Age of AI.
        </p>
      </header>

      {mode === 'home' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card title="Facilitator" desc="Create a new session for your class">
              <button
                className="btn-primary"
                onClick={() => setMode('create')}
              >
                Create Session
              </button>
              <FacilitatorSignInRow />
            </Card>
            <Card title="Team" desc="Join an existing session with your code">
              <button
                className="btn-secondary"
                onClick={() => setMode('join')}
              >
                Join Session
              </button>
            </Card>
          </div>
          <RecentSessionsPanelGate />
        </>
      )}

      {mode === 'create' && (
        <CreateSessionForm
          onCreated={(sessionId) => {
            setFacilitator(sessionId)
            navigate(`/facilitator/${sessionId}`)
          }}
          onBack={() => setMode('home')}
        />
      )}

      {mode === 'join' && (
        <JoinTeamForm
          defaultSessionId={joinId}
          onJoined={(sid, tid, name) => {
            setTeam(sid, tid, name)
            navigate(`/team/${sid}/${tid}`)
          }}
          onBack={() => setMode('home')}
        />
      )}
    </div>
  )
}

// --- Components -----------------------------------------------------------

function Card({ title, desc, children }: {
  title: string; desc: string; children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-xl font-semibold text-slate-800">{title}</h2>
      <p className="text-slate-600 mt-1 mb-4">{desc}</p>
      {children}
    </div>
  )
}

function RecentSessionsPanelGate() {
  const user = useAuthStore((s) => s.user)
  const loading = useAuthStore((s) => s.loading)
  if (loading) {
    return <div className="mt-6 h-24 rounded-lg bg-slate-100 animate-pulse" />
  }
  return user ? <RecentSessionsPanel /> : null
}

/** Sign-in row shown inside the Facilitator card when not signed in.
 * Putting it here (rather than as a top-level panel) makes it clear that
 * accounts are a facilitator feature — team members don't sign up. */
function FacilitatorSignInRow() {
  const user = useAuthStore((s) => s.user)
  const loading = useAuthStore((s) => s.loading)
  if (loading || user) return null
  const next = '/'
  return (
    <div className="mt-4 pt-4 border-t border-slate-200">
      <p className="text-xs text-slate-500 mb-2">
        Sign in to track sessions you create:
      </p>
      <div className="flex flex-wrap gap-2">
        <a
          href={api.loginUrl('google', next)}
          className="btn-secondary !py-1.5 !px-3 text-xs"
        >
          <GoogleIcon className="w-4 h-4 mr-2" />
          Google
        </a>
        <a
          href={api.loginUrl('microsoft', next)}
          className="btn-secondary !py-1.5 !px-3 text-xs"
        >
          <MicrosoftIcon className="w-4 h-4 mr-2" />
          Microsoft
        </a>
      </div>
    </div>
  )
}

function RecentSessionsPanel() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-sessions', 5],
    queryFn: () => api.listMySessions(5),
  })
  return (
    <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-slate-800">
          Your recent sessions
        </h2>
        <Link to="/sessions" className="text-sm text-brand-700 hover:underline">
          See all →
        </Link>
      </div>
      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {error && (
        <div className="text-sm text-danger">Could not load your sessions.</div>
      )}
      {data && data.length === 0 && (
        <p className="text-sm text-slate-500">
          No sessions yet — create one below and it'll show up here.
        </p>
      )}
      {data && data.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {data.map((s) => (
            <RecentSessionRow key={s.id} session={s} />
          ))}
        </ul>
      )}
    </div>
  )
}

function RecentSessionRow({ session }: { session: OwnedSessionInfo }) {
  return (
    <li>
      <Link
        to={`/facilitator/${session.id}`}
        className="flex items-center justify-between py-2 px-1 hover:bg-slate-50 rounded"
      >
        <div className="min-w-0">
          <div className="font-medium text-slate-800 truncate">{session.name}</div>
          <div className="text-xs text-slate-500">
            <StatusBadge status={session.status} />
            <span className="ml-2">
              Q{session.current_quarter} / {session.num_quarters}
            </span>
            <span className="ml-2">{formatRelative(
              session.last_activity_at ?? session.created_at,
            )}</span>
          </div>
        </div>
        <span className="text-sm text-brand-700">Open →</span>
      </Link>
    </li>
  )
}

function StatusBadge({ status }: { status: 'lobby' | 'active' | 'complete' }) {
  const styles: Record<typeof status, string> = {
    lobby:    'bg-slate-100 text-slate-700',
    active:   'bg-brand-100 text-brand-700',
    complete: 'bg-green-100 text-green-700',
  }
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${styles[status]}`}>
      {status}
    </span>
  )
}

function formatRelative(ts: number): string {
  const seconds = Date.now() / 1000 - ts
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)}d ago`
  return new Date(ts * 1000).toLocaleDateString()
}

function CreateSessionForm({
  onCreated, onBack,
}: { onCreated: (sessionId: string) => void; onBack: () => void }) {
  const [name, setName] = useState('Exec Ed Cohort')
  const [numQuarters, setNumQuarters] = useState(20)
  const [competitive, setCompetitive] = useState(true)
  const [scenario, setScenario] = useState('base')
  const [aiTeams, setAITeams] = useState<AITeamConfig[]>([])
  // Empty string = manual-only. Stored as string so the input can be cleared.
  const [minutesPerQuarter, setMinutesPerQuarter] = useState<string>('')

  const { data: scenarios } = useQuery({
    queryKey: ['scenarios'],
    queryFn: api.getScenarios,
  })
  const { data: aiConfig } = useQuery({
    queryKey: ['ai-config'],
    queryFn: api.getAIConfig,
  })
  const { data: aiPersonas } = useQuery({
    queryKey: ['ai-personas'],
    queryFn: api.getAIPersonas,
  })

  const createMut = useMutation({
    mutationFn: api.createSession,
    onSuccess: (s) => onCreated(s.id),
  })

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-xl font-semibold mb-4">Create Session</h2>
      <div className="space-y-4">
        <Field label="Session name">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Quarters">
          <input
            type="number"
            className="input"
            min={1}
            max={40}
            value={numQuarters}
            onChange={(e) => setNumQuarters(parseInt(e.target.value) || 20)}
          />
        </Field>
        <Field label="Minutes per quarter (auto-advance)">
          <input
            type="number"
            className="input"
            min={0.5}
            max={120}
            step={0.5}
            placeholder="Leave blank for manual advance"
            value={minutesPerQuarter}
            onChange={(e) => setMinutesPerQuarter(e.target.value)}
          />
          <p className="text-xs text-slate-500 mt-1">
            When set, each quarter auto-advances once the clock hits zero.
            Facilitator can still advance manually or pause the clock.
          </p>
        </Field>
        <Field label="Scenario">
          <select
            className="input"
            value={scenario}
            onChange={(e) => setScenario(e.target.value)}
          >
            {(scenarios ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          {scenarios?.find((s) => s.id === scenario)?.description && (
            <p className="text-xs text-slate-500 mt-1">
              {scenarios.find((s) => s.id === scenario)?.description}
            </p>
          )}
        </Field>
        <Field label="">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={competitive}
              onChange={(e) => setCompetitive(e.target.checked)}
            />
            <span>Competitive dynamics (industry advances independently)</span>
          </label>
        </Field>

        <AITeamsSection
          config={aiConfig}
          personas={aiPersonas?.personas ?? []}
          aiTeams={aiTeams}
          setAITeams={setAITeams}
        />

        {createMut.isError && (
          <div className="text-danger text-sm">
            {String((createMut.error as Error)?.message)}
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <button className="btn-secondary" onClick={onBack}>Back</button>
          <button
            className="btn-primary"
            disabled={createMut.isPending}
            onClick={() => {
              const parsed = parseFloat(minutesPerQuarter)
              const mpq = Number.isFinite(parsed) && parsed > 0 ? parsed : null
              createMut.mutate({
                name, num_quarters: numQuarters,
                competitive_dynamics_mode: competitive, scenario,
                ai_teams: aiTeams,
                minutes_per_quarter: mpq,
              })
            }}
          >
            {createMut.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  )
}

function JoinTeamForm({
  defaultSessionId, onJoined, onBack,
}: {
  defaultSessionId?: string
  onJoined: (sid: string, tid: string, name: string) => void
  onBack: () => void
}) {
  const [sessionId, setSessionId] = useState(defaultSessionId ?? '')
  const [teamName, setTeamName] = useState('')

  const joinMut = useMutation({
    mutationFn: ({ sid, name }: { sid: string; name: string }) =>
      api.joinTeam(sid, name),
    onSuccess: (t) => onJoined(t.session_id, t.id, t.name),
  })

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-xl font-semibold mb-4">Join Session</h2>
      <div className="space-y-4">
        <Field label="Session code">
          <input
            className="input font-mono"
            placeholder="s-xxxxxxxx"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value.trim())}
          />
        </Field>
        <Field label="Team name">
          <input
            className="input"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder="e.g. The Board of BankCo"
          />
        </Field>
        {joinMut.isError && (
          <div className="text-danger text-sm">
            {String((joinMut.error as Error)?.message)}
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <button className="btn-secondary" onClick={onBack}>Back</button>
          <button
            className="btn-primary"
            disabled={!sessionId || !teamName || joinMut.isPending}
            onClick={() => joinMut.mutate({ sid: sessionId, name: teamName })}
          >
            {joinMut.isPending ? 'Joining…' : 'Join'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      {label && <span className="text-sm text-slate-600">{label}</span>}
      <div className={label ? 'mt-1' : ''}>{children}</div>
    </label>
  )
}

function AITeamsSection({
  config, personas, aiTeams, setAITeams,
}: {
  config: AIConfigResponse | undefined
  personas: PersonaInfo[]
  aiTeams: AITeamConfig[]
  setAITeams: (next: AITeamConfig[]) => void
}) {
  if (!config) return null  // still loading

  const addTeam = () => {
    setAITeams([...aiTeams, { name: null, persona: null }])
  }

  const updateTeam = (i: number, patch: Partial<AITeamConfig>) => {
    const next = aiTeams.slice()
    next[i] = { ...next[i], ...patch }
    setAITeams(next)
  }

  const removeTeam = (i: number) => {
    const next = aiTeams.slice()
    next.splice(i, 1)
    setAITeams(next)
  }

  return (
    <div className="border-t border-slate-200 pt-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-2">AI teams (optional)</h3>
      {!config.enabled ? (
        <p className="text-xs text-slate-500">
          AI teams are not configured. Set
          <code className="font-mono mx-1">AI_PROVIDER</code> and the matching API key
          (e.g. <code className="font-mono">GEMINI_API_KEY</code>) in the server's
          <code className="font-mono mx-1">.env</code> to enable AI opponents.
        </p>
      ) : (
        <>
          {aiTeams.length > 0 && (<p className="text-xs text-slate-500 mb-2">
            Pick an archetype to shape how each team plays.
          </p>)}
          {aiTeams.length > 0 && (
            <div className="space-y-2 mb-2">
              {aiTeams.map((t, i) => (
                <AITeamRow
                  key={i}
                  team={t}
                  personas={personas}
                  onChange={(patch) => updateTeam(i, patch)}
                  onRemove={() => removeTeam(i)}
                />
              ))}
            </div>
          )}
          <button type="button" onClick={addTeam} className="text-xs text-brand-700 hover:underline">
            + Add AI team
          </button>
        </>
      )}
    </div>
  )
}

function AITeamRow({
  team, personas, onChange, onRemove,
}: {
  team: AITeamConfig
  personas: PersonaInfo[]
  onChange: (patch: Partial<AITeamConfig>) => void
  onRemove: () => void
}) {
  return (
    <div className="flex items-center gap-2">
      <select
        aria-label="Archetype"
        className="input flex-1"
        value={team.persona ?? ''}
        onChange={(e) => onChange({ persona: e.target.value || null })}
      >
        <option value="">(default balanced play)</option>
        {personas.map((p) => (
          <option key={p.id} value={p.id}>{p.name} — {p.short}</option>
        ))}
      </select>
      <input
        aria-label="Team name"
        className="input w-56"
        placeholder="Team name (optional)"
        value={team.name ?? ''}
        onChange={(e) => onChange({ name: e.target.value || null })}
      />
      <button
        type="button"
        aria-label="Remove AI team"
        className="text-slate-400 hover:text-danger text-xl leading-none px-2"
        onClick={onRemove}
      >
        ×
      </button>
    </div>
  )
}
